import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';

function compareSortOrder(a, b) {
  const aOrder = a?.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const bOrder = b?.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return (a?.id ?? 0) - (b?.id ?? 0);
}

const CostSheetTreeView = ({ categories, onCostSheetSelect, selectedCostSheetId }) => {
  const [expanded, setExpanded] = useState({});

  const handleToggle = (categoryId) => {
    setExpanded(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const renderCategory = (category, level = 0) => {
    const isExpanded = expanded[category.id];
    const hasChildren = category.children && category.children.length > 0;
    const hasCostSheets = category.costSheets && category.costSheets.length > 0;
    const isExpandable = hasChildren || hasCostSheets;
    const indentLevel = level * 24;

    return (
      <Box key={category.id} sx={{ ml: `${indentLevel}px` }}>
        {/* Category Header */}
        <ListItem
          disablePadding
          sx={{
            minHeight: 40,
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
        >
          <ListItemButton
            onClick={() => isExpandable && handleToggle(category.id)}
            sx={{ 
              py: 0.5,
              pl: 1,
              pr: 0.5,
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {isExpandable ? (
                isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />
              ) : (
                <Box sx={{ width: 20 }} />
              )}
            </ListItemIcon>
            <FolderIcon 
              fontSize="small" 
              sx={{ 
                color: 'primary.main', 
                mr: 1,
                opacity: 0.7 
              }} 
            />
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {category.name}
                  </Typography>
                  {category.code && (
                    <Chip 
                      label={category.code} 
                      size="small" 
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {hasCostSheets && (
                    <Chip 
                      label={`${category.costSheets.length} items`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
            />
          </ListItemButton>
        </ListItem>

        {/* Child Categories and Cost Sheets */}
        {(hasChildren || hasCostSheets) && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {/* Child Categories First */}
              {hasChildren && [...category.children].sort(compareSortOrder).map(child => renderCategory(child, level + 1))}
              
              {/* Cost Sheets After Categories */}
              {hasCostSheets && [...category.costSheets].sort(compareSortOrder).map((costSheet) => (
                <ListItem
                  key={costSheet.id}
                  disablePadding
                  sx={{
                    ml: 4,
                    minHeight: 36,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <ListItemButton
                    onClick={() => onCostSheetSelect(costSheet)}
                    selected={selectedCostSheetId === costSheet.id}
                    sx={{ 
                      py: 0.5,
                      pl: 1,
                      pr: 0.5,
                      borderRadius: 1,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <DescriptionIcon 
                        fontSize="small" 
                        sx={{ 
                          color: selectedCostSheetId === costSheet.id ? 'inherit' : 'text.secondary',
                          opacity: 0.7 
                        }} 
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: selectedCostSheetId === costSheet.id ? 'medium' : 'normal',
                              lineHeight: 1.2
                            }}
                          >
                            {costSheet.code ? `${costSheet.code} – ` : ''}{costSheet.name}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: selectedCostSheetId === costSheet.id ? 'inherit' : 'text.secondary',
                              opacity: 0.8
                            }}
                          >
                            {costSheet.workType} • {costSheet.unit}
                            {costSheet.quantity && ` • Qty: ${costSheet.quantity}`}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  if (!categories || categories.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No categories found for this project.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
      <List disablePadding>
        {[...categories].sort(compareSortOrder).map(category => renderCategory(category))}
      </List>
    </Box>
  );
};

export default CostSheetTreeView;
